import { Config } from './Config';

/**
 * Staff avatar URL a pupil can actually load.
 *
 * GetStaffByStudentId returns logoFileURL pointing at Admin-Api's artifact/ReadImageFile, which
 * requires authentication. A pupil reaches Safe Space through a passcode session with no token, so
 * every avatar came back 401 and rendered as a broken image.
 *
 * Custom-Api already exposes an anonymous proxy for exactly this: Artifact/DownloadLogo signs a
 * short-lived storage URL and streams the file, restricted to the production/ namespace and
 * answering 404 rather than 500 for anything missing. Rewriting the host to that endpoint lets the
 * avatar load without opening Admin-Api to unauthenticated callers.
 *
 * Anything that is not a recognisable Admin-Api artifact URL is returned untouched, so an absolute
 * URL from elsewhere, a data: URI, or an empty value all behave exactly as before.
 */
const toProxiedArtifactUrl = (artifactURL, endpoint) => {
  if (!artifactURL || typeof artifactURL !== 'string') return artifactURL;

  // Only rewrite the Admin-Api artifact endpoint; leave every other URL alone.
  if (!/artifact\/ReadImageFile/i.test(artifactURL)) return artifactURL;

  const match = artifactURL.match(/[?&]fileName=([^&]+)/i);
  if (!match) return artifactURL;

  const base = (Config.BASE_URL || '').replace(/\/+$/, '');
  if (!base) return artifactURL;

  return `${base}/Artifact/${endpoint}?fileName=${match[1]}`;
};

export const toPupilViewableAvatarUrl = (logoFileURL) =>
  toProxiedArtifactUrl(logoFileURL, 'DownloadLogo');

export default toPupilViewableAvatarUrl;

/**
 * A Safe Space message attachment a pupil needs to open.
 *
 * Rewritten for the same reason as the avatar helper: the file is handed to an <img src>, to an
 * XMLHttpRequest, and to an <a download>, none of which can attach an Authorization header, so
 * Admin-Api answers 401 and it will not open. PeopleManagement-App carries the same pair.
 *
 * This was an alias for the avatar helper, so attachments were requested from
 * Artifact/DownloadLogo. That worked only while DownloadLogo's guard admitted anything under
 * production/ — a breadth its own comment disclaimed. The guard has since been narrowed to the logo
 * and avatar namespaces, so attachments started coming back 404 instead of loading: the same file,
 * the same pupil, a different refusal.
 *
 * Attachments have their own endpoint now. Pointing at it keeps the logo proxy narrow and stops a
 * child's uploaded file depending on how loosely an unrelated endpoint happens to be scoped.
 */
export const toPupilViewableArtifactUrl = (attachmentURL) =>
  toProxiedArtifactUrl(attachmentURL, 'DownloadSafeSpaceAttachment');
